/**
 * API service for fetching data from Sanity through our backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

class SanityAPI {
    /**
     * Fetch all products
     */
    static async getProducts(category = null, limit = 100, offset = 0) {
        console.log('='.repeat(80));
        console.log('🟦 FRONTEND API: Calling getProducts');
        console.log(`📊 Params: category=${category}, limit=${limit}, offset=${offset}`);

        try {
            let url = `${API_BASE_URL}/sanity/products?limit=${limit}&offset=${offset}`;
            if (category) {
                url += `&category=${category}`;
            }

            console.log(`🌐 Fetching from: ${url}`);
            const response = await fetch(url);
            console.log(`📡 Response status: ${response.status}`);

            if (!response.ok) {
                console.error(`❌ HTTP Error: ${response.status}`);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log(`✅ Received ${data.products?.length || 0} products`);
            console.log(`📦 First product: ${data.products?.[0]?.name || 'N/A'}`);
            console.log('='.repeat(80));
            return data.products || [];
        } catch (error) {
            console.error('❌ Error fetching products:', error);
            console.log('='.repeat(80));
            return [];
        }
    }

    /**
     * Fetch all categories
     */
    static async getCategories() {
        try {
            const response = await fetch(`${API_BASE_URL}/sanity/categories`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.categories || [];
        } catch (error) {
            console.error('Error fetching categories:', error);
            return [];
        }
    }

    /**
     * Fetch a single product by ID
     */
    static async getProduct(productId) {
        console.log('='.repeat(80));
        console.log(`🟦 FRONTEND API: Calling getProduct(${productId})`);

        try {
            const url = `${API_BASE_URL}/sanity/products/${productId}`;
            console.log(`🌐 Fetching from: ${url}`);

            const response = await fetch(url);
            console.log(`📡 Response status: ${response.status}`);

            if (!response.ok) {
                if (response.status === 404) {
                    console.error(`❌ Product not found: ${productId}`);
                    console.log('='.repeat(80));
                    return null;
                }
                console.error(`❌ HTTP Error: ${response.status}`);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const product = await response.json();
            console.log(`✅ Product fetched: ${product?.name || 'N/A'}`);
            console.log('='.repeat(80));
            return product;
        } catch (error) {
            console.error('❌ Error fetching product:', error);
            console.log('='.repeat(80));
            return null;
        }
    }

    /**
     * Execute a custom GROQ query
     */
    static async query(groqQuery, params = null) {
        try {
            const response = await fetch(`${API_BASE_URL}/sanity/query`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    query: groqQuery,
                    params: params
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.result || [];
        } catch (error) {
            console.error('Error executing query:', error);
            return [];
        }
    }

    /**
     * Create a new product
     */
    static async createProduct(productData) {
        try {
            const response = await fetch(`${API_BASE_URL}/sanity/documents`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    _type: 'product',
                    data: productData
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error creating product:', error);
            throw error;
        }
    }

    /**
     * Update an existing product
     */
    static async updateProduct(productId, productData) {
        try {
            const response = await fetch(`${API_BASE_URL}/sanity/documents/${productId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    data: productData
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error updating product:', error);
            throw error;
        }
    }

    /**
     * Delete a product
     */
    static async deleteProduct(productId) {
        try {
            const response = await fetch(`${API_BASE_URL}/sanity/documents/${productId}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error deleting product:', error);
            throw error;
        }
    }

    /**
     * Check backend health
     */
    static async healthCheck() {
        try {
            const response = await fetch(`${API_BASE_URL}/sanity/health`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Health check failed:', error);
            return { status: 'unhealthy', sanity_connected: false };
        }
    }
}

export default SanityAPI;
